import Constants from "expo-constants";
import { Platform } from "react-native";

export type DeploymentEnvironment = "local" | "preview" | "production";

function resolveDeploymentEnvironment(): DeploymentEnvironment {
  if (Platform.OS === "web") {
    if (typeof window === "undefined") return "local";
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") return "local";
    // Cloudflare Workers: use.<domain> is production, dev-use.<domain> (and
    // any *.workers.dev URL) is preview.
    if (host.startsWith("dev-use.") || host.endsWith(".workers.dev"))
      return "preview";
    return "production";
  }
  // Native builds: `ENVIRONMENT` in .env (or the eas.json build profile) reaches
  // the app through app.config.ts. DEVELOPMENT is the local DDEV stack, TESTING
  // the Coolify preview API; anything else, unset included, is production.
  const requested = String(
    Constants.expoConfig?.extra?.environment ?? "",
  ).toUpperCase();
  return requested === "DEVELOPMENT"
    ? "local"
    : requested === "TESTING"
      ? "preview"
      : "production";
}

const deploymentEnvironment = resolveDeploymentEnvironment();
const sentryAppDsn =
  "https://879d08d5fe25f5d43d5654bb27c321e1@o4504087623761920.ingest.us.sentry.io/4511953515905024";
const turnstileSiteKey = "0x4AAAAAAEsjGxcwu427f0BG";

type EnvironmentConstants = {
  siteUrl: string;
  appUrl: string;
  apiUrl: string;
  sentryAppDsn?: string;
  turnstileSiteKey?: string;
};

const constantsByEnvironment: Record<
  DeploymentEnvironment,
  EnvironmentConstants
> = {
  local: {
    siteUrl: "http://localhost:3000",
    appUrl: "http://localhost:8081",
    apiUrl: "https://gearsync-api.ddev.site",
    // No spam check locally: the widget is not authorised for localhost.
  },
  preview: {
    siteUrl: "https://gearsync-website.pages.dev",
    appUrl: "https://dev-use.gearsync.app",
    apiUrl: "https://dev-api.gearsync.app",
    sentryAppDsn,
    turnstileSiteKey,
  },
  production: {
    siteUrl: "https://www.gearsync.app",
    appUrl: "https://use.gearsync.app",
    apiUrl: "https://api.gearsync.app",
    sentryAppDsn,
    turnstileSiteKey,
  },
};

const current = constantsByEnvironment[deploymentEnvironment];
export const DEPLOYMENT_ENVIRONMENT = deploymentEnvironment;
/** Whether new accounts can be created from the app. */
export const REGISTRATION_ENABLED = true;
export const SITE_URL = current.siteUrl;
export const APP_URL = current.appUrl;
export const SENTRY_APP_DSN = current.sentryAppDsn;
export const TURNSTILE_SITE_KEY = current.turnstileSiteKey;
// Override with EXPO_PUBLIC_API_URL, e.g. for a phone on the LAN.
export const API_URL =
  process.env.EXPO_PUBLIC_API_URL?.trim() || current.apiUrl;
export const backendConfigured = Boolean(API_URL);
export const SENTRY_ENABLED = Boolean(SENTRY_APP_DSN);
export const TURNSTILE_ENABLED = Boolean(TURNSTILE_SITE_KEY);
// RevenueCat Web Billing (Stripe): live keys on production, sandbox keys (Stripe
// test cards) everywhere else.
const REVENUECAT_WEB_KEYS = {
  sandbox: "rcb_sb_qaprdStcXFIqvgkKaeIgRNKSY",
  production: "rcb_EJYhBaxWfdnsIDwLpHilralMWhma",
} as const;

export const REVENUECAT_API_KEYS = {
  ios: "appl_BQxmmxmhubePzwZwAaAtFKKqXCS",
  android: "goog_xGDMGyMaWPnGKddgFXeoLRSzfdI",
  test: "test_PrkqPtfbwFcbLvvOqWwxMZXagUo",
  web:
    DEPLOYMENT_ENVIRONMENT === "production"
      ? REVENUECAT_WEB_KEYS.production
      : REVENUECAT_WEB_KEYS.sandbox,
} as const;
export const DEFAULT_REGIONAL_SETTINGS = {
  units: "metric",
  dateFormat: "en-NZ",
  timezone: "Pacific/Auckland",
} as const;
export const STORE_LINKS = { ios: "", android: "" } as const;
export const EXTERNAL_LINKS = {
  privacyPolicy: `${SITE_URL}/legal/privacy-policy/`,
  terms: `${SITE_URL}/legal/terms-and-conditions/`,
  facebook: "",
  instagram: "",
  help: `${SITE_URL}/help/`,
} as const;
